import { AddonPlatform } from "@coeff/api";
import * as React from "react";
import { connect } from "react-redux";
import { Route, Switch, withRouter } from "react-router-dom";
import styled from "styled-components";

import {
  createQuickstart,
  getDomains,
  getDomainUsers,
  getGlobalSetting,
  getQuickstarts,
  upsertGlobalSetting,
} from "../../actions";
import { CAContext, Domain, Quickstart as QuickstartT, User } from "../../types";
import { getDomain } from "../../utils";
import { WorkspaceAdmin } from "../WorkspaceAdmin/WorkspaceAdmin";

import { Credits, DomainDetails, Domains, Quickstart } from "./components";
import { Sidebar } from "./components/Sidebar";
import { RecipeManager } from "./RecipeManager";
import { SmartAlertsDebug } from "./SmartAlertsDebug/SmartAlertsDebug";
import { ThreadAnalysis } from "./ThreadAnalysis/ThreadAnalysis";

interface CoeffAdminProps {
  createQuickstart: (email: string, addon_platform: AddonPlatform) => void;
  domainDetails: Domain | null;
  domains: Domain[];
  getDomains: (params?: Record<string, string | number>) => void;
  getDomainUsers: (domain: string) => void;
  getGlobalSetting: (type: string) => void;
  getQuickstarts: (email: string) => QuickstartT[];
  globalSettings: { beta_gsuite_domains?: string[] };
  loading: boolean;
  location: any;
  upsertGlobalSetting: (setting: object) => void;
  users: User[];
}

interface CoeffAdminState {
  domain: string;
  selectedDomain: string;
}

const Header = styled.div`
  display: flex;
  align-items: center;
  h1 {
    margin: 0px;
    font-size: 28px;
    font-weight: 800;
  }
  .coeff__select {
    margin-left: 24px;
    .ant-select-selector {
      width: 148px;
      height: 32px;
      background-color: #f5f5f5;
      .ant-select-selection-item {
        font-weight: 600;
      }
    }
  }
`;

const Panel = styled.section`
  padding: 1rem;
  display: inline-block;
  width: 100%;
  height: 100%;
  min-width: 0px;
`;

const Content = styled.div`
  display: inline-block;
  width: 100%;
  height: 100%;
  margin-left: 240px;
  min-width: 0;

  && .ant-table-header table thead tr {
    th {
      background: #ecf6ff;
      color: #0054e6;
      padding-left: 8px;
    }
    & th.ant-table-column-sort {
      background: #ecf6ff;
    }
    & th:hover {
      background: #cce9ff;
    }
  }
`;

const Layout = styled.div`
  display: flex;
  height: 100%;
`;

export const CoeffAdminContext = React.createContext<CAContext | null>(null);

class CoeffAdmin extends React.Component<CoeffAdminProps, CoeffAdminState> {
  dataTab: any;
  quickstartTab: any;
  baseUrl: string;

  constructor(props: CoeffAdminProps) {
    super(props);

    this.state = {
      domain: "",
      selectedDomain: "",
    };

    this.dataTab = React.createRef();
    this.quickstartTab = React.createRef();

    this.baseUrl = "/superadmin";
  }

  componentDidMount = () => {
    const { getDomains, getDomainUsers, getGlobalSetting } = this.props;
    const { selectedDomain } = this.state;
    getGlobalSetting("beta_gsuite_domains");
    getDomains();

    if (!selectedDomain) {
      const domainFromLocation = getDomain();

      if (domainFromLocation) {
        this.setState({ selectedDomain: domainFromLocation });
        getDomainUsers(domainFromLocation);
      }
    } else {
      getDomainUsers(selectedDomain);
    }
  };

  selectDomain = (domain: string) => {
    this.setState({ selectedDomain: domain });

    this.props.getDomainUsers(domain);
  };

  render = () => {
    const { createQuickstart, domainDetails, domains, getQuickstarts, globalSettings, loading } =
      this.props;

    const { selectedDomain } = this.state;

    return (
      <CoeffAdminContext.Provider
        value={{
          createQuickstart,
          domainDetails,
          domains,
          getQuickstarts,
          globalSettings,
          loading,
          selectDomain: this.selectDomain,
          selectedDomain,
          getDomains: this.props.getDomains,
        }}
      >
        <Layout>
          <Sidebar />

          <Content>
            <Switch>
              <Route path={`${this.baseUrl}/domainDetails`}>
                {selectedDomain && <DomainDetails />}

                <WorkspaceAdmin
                  hideHeader={true}
                  coeffAdminDomain={selectedDomain}
                  baseUrl={`${this.baseUrl}/domainDetails`}
                />
              </Route>

              <Route path={`${this.baseUrl}/domains`}>
                <Panel>
                  <Header>
                    <h1>Domains</h1>
                  </Header>
                  <Domains />
                </Panel>
              </Route>

              <Route path="/superadmin/quickstart">
                <Quickstart />
              </Route>

              <Route path="/superadmin/credits">
                <Credits />
              </Route>

              <Route path="/superadmin/recipes">
                <RecipeManager />
              </Route>

              <Route path="/superadmin/smart-alerts-debug">
                <Panel>
                  <SmartAlertsDebug />
                </Panel>
              </Route>

              <Route path="/superadmin/thread-analysis">
                <Panel>
                  <ThreadAnalysis />
                </Panel>
              </Route>
            </Switch>
          </Content>
        </Layout>
      </CoeffAdminContext.Provider>
    );
  };
}

const mapDispatchToProps = (dispatch: any) => ({
  createQuickstart: (email: string, addon_platform: AddonPlatform) =>
    dispatch(createQuickstart(email, addon_platform)),
  getDomains: (params?: Record<string, string | number>) => dispatch(getDomains(params)),
  getDomainUsers: (domain: string) => dispatch(getDomainUsers(domain)),
  getGlobalSetting: (type: string) => dispatch(getGlobalSetting(type)),
  getQuickstarts: (email: string) => dispatch(getQuickstarts(email)),
  upsertGlobalSetting: (setting: object) => dispatch(upsertGlobalSetting(setting)),
});

const mapStateToProps = ({
  coeffAdmin: { domain, domains, loading, users },
  app: { globalSettings },
}) => ({
  domainDetails: domain,
  domains,
  globalSettings,
  loading,
  users,
});

const route = withRouter(connect(mapStateToProps, mapDispatchToProps)(CoeffAdmin));
export { route as CoeffAdmin };
